import { audio, div, image } from "./markup/tags";

/**
 * Pauses program execution for a given amount of time
 * @param {number} ms The number of milliseconds to sleep for
 * @returns A resolved promise after ms milliseconds
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Add a random number between 0 and offset to the base number
 * @param {number} base The starting number
 * @param {number} offset The maximum addition to base
 * @returns The base number jittered by the offset
 */
export function jitter(base, offset) {
  return base + Math.floor(Math.random() * Math.floor(offset));
}

/**
 * Add a random number between 0 and 50 to the base number
 * @param {number} base The starting number
 * @returns The base number jittered by 50
 */
export function jitter50(base) {
  return jitter(base, 50);
}

/**
 * Flips a coin
 * @returns Returns true or false randomly
 */
export function randomTrue() {
  return Math.random() > 0.5;
}

/**
 * Deeply copies an object
 * @param {Object} obj The starting object
 * @returns An exact copy of obj
 */
export function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Format a number as a US dollar amount
 * @param {number} amount Dollar amount
 * @returns The string representation of amount in USD
 */
export function formatDollars(amount) {
  return "$" + parseFloat(amount).toFixed(2);
}

/**
 * Interleave a value before/after every element in an array
 * @param {Array<any>} arr The original array
 * @param {any} val The value to interleave inside the array
 * @param {boolean} addBefore Whether to add val before or after each element in array
 * @returns The original array with val interleaved between every element
 */
export function interleave(arr, val, addBefore = true) {
  return [].concat(...arr.map((n) => (addBefore ? [val, n] : [n, val])));
}

/**
 * Retrieves JSON data from an external URL
 * @param {string} url The URL to fetch data from
 * @returns {Object} The JSON data fetched from the URL
 */
export async function loadExternalJSON(url) {
  try {
    // Fetch the data from the URL
    const response = await fetch(url, { cache: "no-store" });

    // Check if the response is ok (status is 200-299)
    if (!response.ok) {
      console.error("Bad response when loading data:", response);
      throw new Error("Network response was not ok: " + response.statusText);
    }
    return await response.json();
  } catch (error) {
    // TODO: Failover JSON file in the repo if unable to load the data
    // Handle any errors that occur during the fetch or response parsing
    console.error("There has been a problem with your fetch operation:", error);
    throw new Error("Failed to load the file at " + url);
  }
}

/**
 * Randomly retrieves a single element from an array
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Array} array A given array of elements
 * @returns
 */
export function getRandomElement(jsPsych, array) {
  return jsPsych.randomization.sampleWithoutReplacement(array, 1)[0];
}

/**
 * Retrieves the data object from the current trial in JsPsych
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @returns
 */
export function getCurrentTrialData(jsPsych) {
  return jsPsych.getCurrentTrial().data;
}

/**
 * Retrieves the data object from the last trial in JsPsych
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @returns {Object}
 */
export function getLastTrialData(jsPsych) {
  const dataCollection = jsPsych.data.getLastTrialData();
  return dataCollection.trials[0];
}
/// Default keyboard layout config used if layout detection did not run during setup
const DEFAULT_LAYOUT_CONFIG = {
  id: "QWERTY_USUK",
  origin: "USUK",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyK: "K",
  KeyL: "L",
  Semicolon: ";",
  forwardSpeedUpKey: "/",
  backwardSlowDownKey: "Z",
  replayKey: "R",
  spaceKey: "Space",
  keyboardLayoutHeadquarterSrc: "assets/images/headquarter/QWERTY_USUK/",
  spaceBarFeedbackKeysM1: "assets/images/instructions/m1/QWERTY_USUK/",
};

/**
 * Retrieves the keyboard layout config detected during the setup flow.
 * Falls back to QWERTY_USUK if layout detection did not run.
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @returns {Object}
 */
export function getSelectedKeyboardLayoutConfig(jsPsych) {
  if (!jsPsych?.data?.get) return DEFAULT_LAYOUT_CONFIG;

  const lastLayoutTrial = jsPsych
    .data
    .get()
    .filter({ name: "keyboardLayoutDetection" })
    .last(1)
    .values()[0];

  return lastLayoutTrial?.layout_config ?? DEFAULT_LAYOUT_CONFIG;
}

/**
 * Resolves keyboard-layout placeholders in text using the active layout config.
 * Supported placeholders:
 * - __FORWARD_KEY__
 * - __BACKWARD_KEY__
 * - __REPLAY_KEY__
 * - __SPACE_KEY__
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {string} text The input text that may contain placeholders
 * @returns {string}
 */
export function resolveLayoutPlaceholders(jsPsych, text) {
  if (typeof text !== "string") return text;

  const layoutConfig = getSelectedKeyboardLayoutConfig(jsPsych);
  const backwardSlowDownKeyLabel = layoutConfig?.backwardSlowDownKey ?? "Z";
  const forwardSpeedUpKeyLabel = layoutConfig?.forwardSpeedUpKey ?? "/";
  const replayKeyLabel = layoutConfig?.replayKey ?? "R";
  const spaceKeyLabel = layoutConfig?.spaceKey ?? "Space";

  return text
    .replace(/__BACKWARD_KEY__/g, backwardSlowDownKeyLabel)
    .replace(/__FORWARD_KEY__/g, forwardSpeedUpKeyLabel)
    .replace(/__REPLAY_KEY__/g, replayKeyLabel)
    .replace(/__SPACE_KEY__/g, spaceKeyLabel);
}

/**
 * Resolves keyboard layout placeholders inside the `#tutorialHeader` DOM element (if present).
 * @param {JsPsych} jsPsych
 */
export function applyTutorialHeaderPlaceholders(jsPsych) {
  const tutorialHeaderEl = document.getElementById("tutorialHeader");
  if (!tutorialHeaderEl) return;
  tutorialHeaderEl.innerHTML = resolveLayoutPlaceholders(jsPsych, tutorialHeaderEl.innerHTML);
}

/**
 * Creates a file path by combining a folder path and a filename.
 * @param {string} folderPath Folder path ending with or without '/'
 * @param {string} fileName File name only
 * @returns {string}
 */
export function buildPathFromFolder(folderPath, fileName) {
  if (!folderPath) return fileName;
  return `${folderPath.endsWith("/") ? folderPath : `${folderPath}/`}${fileName}`;
}

const DEFAULT_KEYBOARD_LAYOUT_HEADQUARTER_FOLDER = "assets/images/headquarter/QWERTY_USUK/";

export function getAgentFileBasename(agentFile) {
  if (typeof agentFile !== "string") return "";
  const parts = agentFile.split("/");
  return parts[parts.length - 1] || "";
}

export function normalizeAgentAssetKey(agentFile) {
  return getAgentFileBasename(agentFile).replace(/\.svg$/i, "");
}

export function isLaneLabelAgentAsset(agentFile) {
  if (typeof agentFile !== "string") return false;
  const normalized = normalizeAgentAssetKey(agentFile);
  return normalized.startsWith("footer_LaneLabels");
}

export function resolveLaneLabelAgentAssetPath(layoutConfig, agentFile) {
  const folder =
    layoutConfig?.keyboardLayoutHeadquarterSrc
    ?? DEFAULT_KEYBOARD_LAYOUT_HEADQUARTER_FOLDER;
  return buildPathFromFolder(folder, getAgentFileBasename(agentFile));
}

/**
 * Generates an image tag specific to the Gearshift instruction pages
 * @param {string} src The path to the image's src
 */
export function instructionImage(src) {
  return image({
    src,
    alt: "An image with some instructions",
    style: "width: 85%; height: 85%",
  });
}

/** The id corresponding to the image tag on an instructions screen */
export const INSTRUCTIONS_IMAGE_ID = "instructions_image";
/** The id corresponding to the audio tag on an instructions screen */
export const INSTRUCTIONS_AUDIO_ID = "instructions_audio";

/**
 * Generates the HTML specific to the Gearshift instruction pages
 * @param {string} imageSrc The path to the image file's src
 * @param {string | undefined} [audioSrc] Optionally, the path to the audio file's src
 * @param {Object} [overlayOptions] Optional per-page overlay settings
 * @param {number} [overlayOptions.overlayLeftPercent] Overlay X position in % (0-100)
 * @param {number} [overlayOptions.overlayTopPercent] Overlay Y position in % (0-100)
 * @param {string} [overlayOptions.overlayNavText] Overlay text content
 * @param {boolean} [overlayOptions.showBackKey] Whether to show the back-key hint in the overlay
 * @param {boolean} [overlayOptions.showForwardKey] Whether to show the forward-key hint in the overlay
 */
export function instructionPage(imageSrc, audioSrc, overlayOptions = {}) {
  let audioTag = "";
  if (audioSrc) {
    audioTag = audio({ id: INSTRUCTIONS_AUDIO_ID, src: audioSrc, autoplay: true, style: "hidden" });
  }

  const containerAttributes = {};
  if (overlayOptions.overlayLeftPercent !== undefined) {
    containerAttributes["data-overlay-left-percent"] = overlayOptions.overlayLeftPercent;
  }
  if (overlayOptions.overlayTopPercent !== undefined) {
    containerAttributes["data-overlay-top-percent"] = overlayOptions.overlayTopPercent;
  }
  if (overlayOptions.overlayNavText !== undefined) {
    containerAttributes["data-overlay-nav-text"] = overlayOptions.overlayNavText;
  }
  if (overlayOptions.showBackKey !== undefined) {
    containerAttributes["data-overlay-show-back-key"] = String(overlayOptions.showBackKey);
  }
  if (overlayOptions.showForwardKey !== undefined) {
    containerAttributes["data-overlay-show-forward-key"] = String(overlayOptions.showForwardKey);
  }
  if (overlayOptions.overlayTextSizeVw !== undefined) {
    containerAttributes["data-overlay-text-size-vw"] = overlayOptions.overlayTextSizeVw;
  }

  return div(
    audioTag +
    image({
      id: INSTRUCTIONS_IMAGE_ID,
      src: imageSrc,
      alt: "An image with some instructions",
      style: "width: 85%; height: 85%",
    }),
    containerAttributes
  );
}

/**
 * Calculates the number of pixels a given percentage of the screen's width is
 * @param {number} percent What percentage of the screen width we want (0-1)
 * @returns The width of the user's screen multiplied by the percentage
 */
export function getScreenWidth(percent = 1) {
  return screen.width * percent;
}

/**
 * Calculates the number of pixels a given percentage of the screen's height is
 * @param {number} percent What percentage of the screen height we want (0-1)
 * @returns The height of the user's screen multiplied by the percentage
 */
export function getScreenHeight(percent = 1) {
  return screen.height * percent;
}

/**
 * Validates a mission config structure and content
 * @param {Object} missionConfig The mission config to validate
 * @param {number} missionNumber The mission number to compare with
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateMissionConfig(missionConfig, missionNumber) {
  // Check if missionConfig has trials
  if (!missionConfig.trials || !Array.isArray(missionConfig.trials)) {
    return {
      isValid: false,
      error: `Invalid mission ${missionNumber} config: trials field is missing or not an array`,
    };
  }

  if (missionConfig.trials.length === 0) {
    return {
      isValid: false,
      error: `Invalid mission ${missionNumber} config: No trials found in configuration`,
    };
  }

  if (missionConfig.mission !== missionNumber) {
    return {
      isValid: false,
      error: `Invalid mission ${missionNumber} config: Mission number does not match`,
    };
  }

  return { isValid: true, error: null };
}

/**
 * Retrieves a given mission config fail
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The metadata being used to build the experiment
 * @param {number} missionNumber Which mission we're getting the config for
 * @returns
 */
export async function getGearshiftMissionConfig(jsPsych, metadata, missionNumber) {
  const index = missionNumber; // - 1; //not needed since we have now a mission 0
  const missionConfigUrls = metadata.missionTaskName[index].taskName;

  // Array is empty, we're skipping the mission
  if (missionConfigUrls.length === 0) return undefined;

  const missionConfigUrl = getRandomElement(jsPsych, missionConfigUrls);
  const missionConfig = await loadExternalJSON(missionConfigUrl);

  // Validate mission config
  const validationResult = validateMissionConfig(missionConfig, missionNumber);
  if (!validationResult.isValid) {
    console.error("❌ Mission config validation failed:", validationResult.error);
    throw new Error(validationResult.error);
  }

  return missionConfig;
}

const CLOUD_VIDEO_BUCKET_BASE_URL = "https://storage.googleapis.com/gf-assets-bucket/instructions/videos";
const CLOUD_VIDEO_ASSET_PREFIX = "instructions/videos";
const CLOUD_SCREENSHOT_BUCKET_BASE_URL = "https://storage.googleapis.com/gf-assets-bucket/instructions/screenshots";
const CLOUD_CUSTOM_SCREENSHOT_PREFIX = "instructions/screenshots";

/**
 * Resolves a video asset path to its Google Cloud Storage URL.
 * Accepts bucket-relative paths under "instructions/videos".
 * @param {string} videoPath - e.g. "instructions/videos/m1/1_solve.mp4"
 * @returns {string}
 */
export function resolveCloudVideoUrl(videoPath) {
  if (typeof videoPath !== "string") return videoPath;
  if (/^https?:\/\//i.test(videoPath)) return videoPath;

  const normalizedPath = videoPath.startsWith(CLOUD_VIDEO_ASSET_PREFIX)
    ? videoPath.slice(CLOUD_VIDEO_ASSET_PREFIX.length).replace(/^\//, "")
    : videoPath.replace(/^\//, "");

  return `${CLOUD_VIDEO_BUCKET_BASE_URL}/${normalizedPath}`;
}

/**
 * Resolves a custom instruction screenshot path to its Google Cloud Storage URL.
 * Accepts bucket-relative paths under "instructions/screenshots".
 * @param {string} screenshotPath - e.g. "instructions/screenshots/screenshots_m0/instructions 0.01.svg"
 * @returns {string}
 */
export function resolveCloudScreenshotUrl(screenshotPath) {
  if (typeof screenshotPath !== "string") return screenshotPath;
  if (/^https?:\/\//i.test(screenshotPath)) return screenshotPath;

  const normalizedPath = screenshotPath.startsWith(CLOUD_CUSTOM_SCREENSHOT_PREFIX)
    ? screenshotPath.slice(CLOUD_CUSTOM_SCREENSHOT_PREFIX.length).replace(/^\//, "")
    : screenshotPath.replace(/^\//, "");

  return `${CLOUD_SCREENSHOT_BUCKET_BASE_URL}/${normalizedPath}`;
}

/**
 * Resolves a base video URL to its layout-specific variant at runtime.
 * Reads window._gear_layoutConfigId, so must be called at trial runtime (e.g. inside on_load).
 * Returns the base URL unchanged if no variant exists for the current layout.
 * @param {string} baseUrl - e.g. "instructions/videos/m1/1_solve.mp4"
 * @returns {string}
 */
export function getLayoutVideoUrl(baseUrl) {
  const VIDEO_SUFFIX_BY_LAYOUT = { AZERTY_FR: "_FR", QWERTZ_DECH: "_DE" };
  const LAYOUT_ASSET_FALLBACKS = { ABNT2_BR: "QWERTY_USUK" };
  const layoutId = window._gear_layoutConfigId ?? "QWERTY_USUK";
  const resolvedId = LAYOUT_ASSET_FALLBACKS[layoutId] ?? layoutId;
  const suffix = VIDEO_SUFFIX_BY_LAYOUT[resolvedId];
  const resolvedBaseUrl = resolveCloudVideoUrl(baseUrl);
  
  if (!suffix) return resolvedBaseUrl;
  const dotIndex = resolvedBaseUrl.lastIndexOf(".");
  if (dotIndex === -1) return resolvedBaseUrl;
  return resolvedBaseUrl.slice(0, dotIndex) + suffix + resolvedBaseUrl.slice(dotIndex);
}

/**
 * Resolves media URL values that may be provided directly or via function.
 * @param {string | (() => string)} mediaUrlOrResolver
 * @returns {string}
 */
export function resolveMediaUrl(mediaUrlOrResolver) {
  return typeof mediaUrlOrResolver === "function"
    ? mediaUrlOrResolver()
    : mediaUrlOrResolver;
}
